import { ArrowRight, Loader2, Trash2 } from 'lucide-react'
import { useEffect } from 'react'

import { Combobox } from '@/components/Combobox'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  ApiError,
  MODES,
  MODE_LABEL,
  getConverter,
  type FieldCopyMode,
  type PairDraft,
} from '@/lib/fieldCopy'

const NO_DEPS: Record<string, string> = {}
const LOOKUP_DEBOUNCE_MS = 400

/** Grid shared by the pair header and every pair row. */
export const PAIR_GRID = 'grid items-start gap-x-3 grid-cols-[28px_minmax(0,1fr)_16px_minmax(0,1fr)_200px_32px]'

function ConverterLine({ pair }: { pair: PairDraft }) {
  if (!pair.source || !pair.target) {
    return <p className="font-mono text-[11.5px] text-text-3">pick a source and a target field</p>
  }
  if (pair.checking) {
    return (
      <p className="inline-flex items-center gap-1.5 font-mono text-[11.5px] text-text-2">
        <Loader2 className="size-3 animate-spin" aria-hidden />
        checking converter…
      </p>
    )
  }
  if (pair.error) {
    return <p className="font-mono text-[11.5px] text-danger">{pair.error}</p>
  }
  const info = pair.info
  if (!info) return null
  const kinds = `${info.source.kind} → ${info.target.kind}`
  if (!info.converter) {
    return (
      <p className="font-mono text-[11.5px] text-danger">
        no converter: {info.reason ?? kinds} — this pair cannot be copied
      </p>
    )
  }
  return (
    <p className="font-mono text-[11.5px] text-text-2">
      <span className="text-ok">converter: {info.label}</span> · {kinds}
    </p>
  )
}

/** One source → target pair as a table row: fields, mode, converter check below. */
export function PairEditor({
  index,
  pair,
  disabled,
  canRemove,
  onChange,
  onRemove,
}: {
  index: number
  pair: PairDraft
  disabled: boolean
  canRemove: boolean
  onChange: (key: number, patch: Partial<PairDraft>) => void
  onRemove: (key: number) => void
}) {
  // the raw input may carry spaces while a name is typed; ids are looked up trimmed
  const { key } = pair
  const source = pair.source.trim()
  const target = pair.target.trim()

  // Converter lookup per pair: debounced, the latest pair of ids wins.
  useEffect(() => {
    if (!source || !target) {
      onChange(key, { info: null, error: null, checking: false })
      return
    }
    onChange(key, { checking: true })
    let cancelled = false
    const timer = window.setTimeout(() => {
      getConverter(source, target)
        .then((info) => {
          if (!cancelled) onChange(key, { info, error: null, checking: false })
        })
        .catch((err: unknown) => {
          if (!cancelled) {
            const error = err instanceof ApiError ? err.detail : (err as Error).message
            onChange(key, { info: null, error, checking: false })
          }
        })
    }, LOOKUP_DEBOUNCE_MS)
    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [key, source, target, onChange])

  const n = index + 1

  return (
    <div className="border-b border-line-row py-2">
      <div className={PAIR_GRID}>
        <span className="pt-1.5 font-mono text-[12px] text-text-3">{n}</span>
        <Combobox
          source="field"
          ariaLabel={`Source field ${n}`}
          disabled={disabled}
          deps={NO_DEPS}
          multi={false}
          value={pair.source}
          onChange={(v) => onChange(key, { source: v })}
          className=""
        />
        <ArrowRight className="mt-2 size-4 text-text-3" aria-hidden />
        <Combobox
          source="field"
          ariaLabel={`Target field ${n}`}
          disabled={disabled}
          deps={NO_DEPS}
          multi={false}
          value={pair.target}
          onChange={(v) => onChange(key, { target: v })}
          className=""
        />
        <Select
          value={pair.mode}
          items={MODE_LABEL}
          disabled={disabled}
          onValueChange={(v) => {
            if (typeof v === 'string') onChange(key, { mode: v as FieldCopyMode })
          }}
        >
          <SelectTrigger size="sm" className="w-full font-mono text-[12px]" aria-label={`Mode ${n}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {MODES.map((m) => (
              <SelectItem key={m} value={m} disabled={m === 'append' && !pair.info?.append_allowed}>
                {MODE_LABEL[m]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label={`Remove pair ${n}`}
          disabled={!canRemove || disabled}
          onClick={() => onRemove(key)}
        >
          <Trash2 aria-hidden />
        </Button>
      </div>
      <div className={`${PAIR_GRID} mt-1`}>
        <span />
        <div className="col-span-4"><ConverterLine pair={pair} /></div>
      </div>
    </div>
  )
}
